import type { Horse, HorseHistoryItem, HorsePollRound } from '../../types'
import { useT } from '../../contexts/LocaleContext'

function roundTitle(
  round: HorsePollRound | undefined,
  t: (key: string, vars?: Record<string, string | number | boolean | null | undefined>) => string,
): string {
  if (!round) return t('horse.historyNoRound')
  const title = (round.title || '').trim()
  const defaults = new Set(['ورس لیگ', 'هورس لیگ', 'سه در چهار', 'دو قلو', 'سه تا یکی', 'شانس بیشتر', 'نظرسنجی اسب'])
  if (!title || defaults.has(title)) return t(`horse.mode.${round.mode}.label`)
  return title
}

function statusLabel(
  item: HorseHistoryItem,
  t: (key: string, vars?: Record<string, string | number | boolean | null | undefined>) => string,
): string {
  const round = item.round
  if (!round) return t('horse.historyPending')
  if (round.status === 'settled') return t('horse.historyStatusSettled')
  if (round.voting_open) return t('horse.historyStatusOpen')
  return t('horse.historyStatusWaiting')
}

function horseIndex(round?: HorsePollRound): Map<number, Horse> {
  const map = new Map<number, Horse>()
  for (const rc of round?.courses || []) {
    for (const horse of rc.course?.horses || []) map.set(horse.id, horse)
  }
  for (const locked of round?.locked_horses || []) {
    if (locked.horse) map.set(locked.horse_id, locked.horse)
  }
  return map
}

function coinLabel(
  type: string | undefined,
  t: (key: string, vars?: Record<string, string | number | boolean | null | undefined>) => string,
): string {
  if (!type) return ''
  const key = `coins.${type}`
  const label = t(key)
  return label === key ? type : label
}

function horseLabel(horse: Horse | undefined, id: number): string {
  if (!horse) return `#${id}`
  return `#${horse.number} ${horse.name}`
}

export function HorseHistoryList({
  items,
  numberLocale,
  onOpen,
}: {
  items: HorseHistoryItem[]
  numberLocale: string
  onOpen: (item: HorseHistoryItem) => void
}) {
  const t = useT()
  return (
    <div className="lucky-horse__list lucky-horse__list--history">
      <h2 className="lucky-horse__list-title">{t('horse.historyTitle')}</h2>
      {items.length === 0 && <div className="lucky-horse__empty">{t('horse.historyEmpty')}</div>}
      {items.map((item) => {
        const when = item.entry.created_at
          ? new Date(item.entry.created_at).toLocaleString(numberLocale)
          : ''
        return (
          <article key={item.entry.id} className="lucky-horse__round">
            <div>
              <h3>{roundTitle(item.round, t)}</h3>
              <p>
                {item.round ? t(`horse.mode.${item.round.mode}.label`) : ''}
                {when ? ` · ${when}` : ''}
                {' · '}
                {statusLabel(item, t)}
                {item.round?.status === 'settled' || item.entry.score > 0
                  ? ` · ${t('horse.historyScore', { score: item.entry.score })}`
                  : ''}
                {item.entry.is_winner && item.entry.prize_amount
                  ? t('horse.entryWinner', {
                      amount: item.entry.prize_amount,
                      type: coinLabel(item.entry.prize_type, t),
                    })
                  : ''}
              </p>
            </div>
            <button type="button" className="lucky-horse__join" onClick={() => onOpen(item)}>
              {t('horse.historyView')}
            </button>
          </article>
        )
      })}
    </div>
  )
}

export function HorseHistoryDetail({
  item,
  numberLocale,
  onBack,
  onOpenRound,
}: {
  item: HorseHistoryItem
  numberLocale: string
  onBack: () => void
  onOpenRound?: (round: HorsePollRound) => void
}) {
  const t = useT()
  const round = item.round
  const horses = horseIndex(round)
  const picks = item.entry.selections || {}
  const ordered = round?.mode === 'three_in_four'

  return (
    <div className="lucky-horse__detail">
      <div className="lucky-horse__detail-top">
        <h2>{roundTitle(round, t)}</h2>
        <button type="button" className="lucky-horse__back" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <div className="lucky-horse__alert lucky-horse__alert--ok">
        {statusLabel(item, t)}
        {' · '}
        {t('horse.historyScore', { score: item.entry.score })}
        {item.entry.correct_count > 0
          ? ` · ${t('horse.historyCorrect', { n: item.entry.correct_count })}`
          : ''}
        {item.entry.is_winner && item.entry.prize_amount
          ? t('horse.entryWinner', {
              amount: item.entry.prize_amount,
              type: coinLabel(item.entry.prize_type, t),
            })
          : ''}
      </div>

      {!round && <p className="lucky-horse__empty">{t('horse.historyNoRound')}</p>}

      {(round?.courses || []).map((rc) => {
        const course = rc.course
        if (!course) return null
        const pickedIds = picks[String(course.id)] || []
        const pickedSet = new Set(pickedIds)
        const result = (course.horses || [])
          .filter((h) => h.finish_position != null && h.finish_position > 0)
          .slice()
          .sort((a, b) => (a.finish_position || 0) - (b.finish_position || 0))
        return (
          <section key={rc.id} className="lucky-horse__card">
            <div className="lucky-horse__course-head">
              <h3>{course.name}</h3>
              <p>{new Date(course.start_time).toLocaleString(numberLocale)}</p>
            </div>

            <h4 className="lucky-horse__hist-label">{t('horse.historyYourPicks')}</h4>
            {pickedIds.length === 0 && <p className="lucky-horse__hint">{t('horse.historyEmpty')}</p>}
            {pickedIds.map((id, index) => {
              const horse = horses.get(id)
              return (
                <div key={id} className="lucky-horse__hist-row is-mine">
                  <span className="lucky-horse__hist-main">
                    {horse?.photo_url && <img src={horse.photo_url} alt="" className="lucky-horse__photo" />}
                    <strong>{horseLabel(horse, id)}</strong>
                  </span>
                  <span className="lucky-horse__hist-tag">
                    {ordered ? t('horse.historyPredicted', { n: index + 1 }) : t('horse.historyMine')}
                    {horse?.finish_position
                      ? ` · ${t('horse.finishPosition', { n: horse.finish_position })}`
                      : ''}
                  </span>
                </div>
              )
            })}

            <h4 className="lucky-horse__hist-label">{t('horse.historyOfficial')}</h4>
            {result.length === 0 && <p className="lucky-horse__hint">{t('horse.historyPending')}</p>}
            {result.map((horse) => (
              <div key={horse.id} className={`lucky-horse__hist-row${pickedSet.has(horse.id) ? ' is-mine' : ''}`}>
                <span className="lucky-horse__hist-main">
                  {horse.photo_url && <img src={horse.photo_url} alt="" className="lucky-horse__photo" />}
                  <strong>
                    {t('horse.finishPosition', { n: horse.finish_position || 0 })}
                    {' · '}
                    {horseLabel(horse, horse.id)}
                  </strong>
                </span>
                {pickedSet.has(horse.id) && <span className="lucky-horse__hist-tag">{t('horse.historyMine')}</span>}
              </div>
            ))}
          </section>
        )
      })}

      {round && onOpenRound && (
        <button type="button" className="lucky-horse__cta" onClick={() => onOpenRound(round)}>
          {t('horse.historyOpenPoll')}
        </button>
      )}
    </div>
  )
}
